import { Component, signal, computed, input, OnInit, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { MemberModel } from '../../../../core/models/data/member.model';
import { RoleModel } from '../../../../core/models/data/role.model';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { MemberService } from '../../../../core/services/member.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { MemberListSortOptions } from '../../../../core/enums/sort-options.enums';
import { MemberListPage } from '../../../member/components/member-list/member-list.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes, MemberListViewLocations } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DisableWithoutPermissionDirective } from "../../../../core/directives/disable-without-permission/disable-without-permission.directive";
import { RolePermission } from '../../../../core/enums/permissions.enums';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';

@Component({
  selector: 'ms-transfer-organization-ownership',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, MemberListPage, ConfirmationModalComponent, InfoModalComponent, DisableWithoutPermissionDirective],
  templateUrl: './transfer-organization-ownership.component.html'
})
export class TransferOrganizationOwnershipComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private memberService = inject(MemberService);
  private userSessionService = inject(UserSessionService);
  private bootstrapService = inject(BootstrapService);

  public organization = input.required<OrganizationModel>();

  public isLoadingRoles = signal<boolean>(false);
  public isLoadingOwner = signal<boolean>(false);
  public currentOwner = signal<MemberModel | undefined>(undefined);
  public errorMessage = signal<string | undefined>(undefined);
  public ownerRoleId = signal<number | undefined>(undefined);
  public orgAdminRoleId = signal<number | undefined>(undefined);

  // Transfer ownership (used when the organization already has an owner).
  public selectedMemberForTransfer = signal<MemberModel | undefined>(undefined);
  public isTransferring = signal<boolean>(false);

  // Assign owner (used when the organization has no owner yet — Staff/Sys-Admin only).
  public selectedMemberForAssign = signal<MemberModel | undefined>(undefined);
  public isAssigning = signal<boolean>(false);

  public isLoading = computed(() => this.isLoadingRoles() || this.isLoadingOwner());
  public MemberListViewLocations = MemberListViewLocations;

  public RolePermission = RolePermission;

  @ViewChild('transferMemberList') transferMemberList?: MemberListPage;
  @ViewChild('assignMemberList') assignMemberList?: MemberListPage;
  @ViewChild('transferConfirmationModalRef') transferConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('assignConfirmationModalRef') assignConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('transferInfoModalRef') transferInfoModalRef!: InfoModalComponent;
  @ViewChild('assignInfoModalRef') assignInfoModalRef!: InfoModalComponent;

  async ngOnInit(): Promise<void> {
    await this.loadCurrentOwner();
  }

  private async loadCurrentOwner(): Promise<void> {
    this.isLoadingRoles.set(true);
    this.isLoadingOwner.set(true);
    this.errorMessage.set(undefined);

    try {
      const rolesResult = await this.authorizationService.getRoles(false);

      if (!rolesResult.success || !rolesResult.data) {
        this.errorMessage.set(rolesResult.message || 'Failed to load roles.');
        return;
      }

      this.isLoadingRoles.set(false);

      //TODO: DEV-1315 - Create enums for RoleNames
      const ownerRole = rolesResult.data.find(
        (role: RoleModel) => role.name.toLowerCase() === 'organization owner'
      );

      //TODO: DEV-1315 - Create enums for RoleNames
      const adminRole = rolesResult.data.find(
        (role: RoleModel) => role.name.toLowerCase() === 'organization administrator'
      );

      if (!ownerRole) {
        this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_OWNER_ROLE_NOT_FOUND());
        return;
      }

      this.ownerRoleId.set(ownerRole.roleId);

      if (adminRole) {
        this.orgAdminRoleId.set(adminRole.roleId);
      }

      const memberFilter: MembersFilter = {
        organizationIds: [this.organization().organizationId],
        roleIds: [ownerRole.roleId],
        statuses: [],
        includeDeletedMembers: false
      };

      const membersResult = await this.memberService.searchMembers(memberFilter, MemberListSortOptions.NameASC, 0, 1);

      if (!membersResult.success || !membersResult.data) {
        this.errorMessage.set(membersResult.message || OrganizationsMessagingConstants.ORGANIZATIONS_OWNER_NOT_FOUND());
        return;
      }

      // An organization with no owner is a valid state (e.g. Staff just created it from scratch).
      // Leave currentOwner undefined so the template offers the Staff/Sys-Admin "Assign Owner" flow.
      if (membersResult.data.data && membersResult.data.data.length > 0) {
        this.currentOwner.set(membersResult.data.data[0]);
      }
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_OWNER_NOT_FOUND(), error);
      this.errorMessage.set(OrganizationsMessagingConstants.ORGANIZATIONS_OWNER_NOT_FOUND());
    } finally {
      this.isLoadingRoles.set(false);
      this.isLoadingOwner.set(false);
    }
  }

  // #region Transfer Ownership
  public openTransferSelector(): void {
    this.transferMemberList?.loadMembers(false);
    this.bootstrapService.manageModal('#transferOwnershipMemberSelectorModal', ModalActions.Show);
  }

  public onMemberSelectedForTransfer(member: MemberModel): void {
    this.selectedMemberForTransfer.set(member);
    this.bootstrapService.manageModal('#transferOwnershipMemberSelectorModal', ModalActions.Hide);

    this.transferConfirmationModalRef.modalType.set(ModalTypes.Question);

    const confirmationMessageData = { firstName: member.firstName, lastName: member.lastName };
    const confirmationMessage =
      OrganizationsMessagingConstants.ORGANIZATIONS_TRANSFER_OWNERSHIP_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
    this.transferConfirmationModalRef.message.set(confirmationMessage);

    this.transferConfirmationModalRef.enableTypeToConfirm.set(true);
    this.transferConfirmationModalRef.typeToConfirmText.set('TRANSFER OWNERSHIP');
    this.transferConfirmationModalRef.confirmButtonText.set('Transfer Ownership');
    this.transferConfirmationModalRef.confirmButtonClassName.set('btn-danger');

    this.bootstrapService.manageModal('#transferOwnershipConfirmationModal', ModalActions.Show);
  }

  public async onTransferConfirmed(): Promise<void> {
    const memberToTransferTo = this.selectedMemberForTransfer();
    if (!memberToTransferTo) return;

    this.isTransferring.set(true);

    try {
      const result = await this.memberService.transferOrganizationOwnership(memberToTransferTo.memberId);

      this.transferInfoModalRef.message.set(result.message);
      if (result.success) {
        const activeUser = this.userSessionService.activeUser();
        const previousOwner = this.currentOwner();

        if (activeUser && previousOwner && activeUser.userId === previousOwner.userId) {
          await this.authorizationStateService.refreshContext();
        }

        this.currentOwner.set(memberToTransferTo);

        this.transferInfoModalRef.modalType.set(ModalTypes.Success);
      } else {
        this.transferInfoModalRef.modalType.set(ModalTypes.Failure);
      }
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_TRANSFERING_OWNERSHIP(), error);
      this.transferInfoModalRef.modalType.set(ModalTypes.Failure);
      this.transferInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_TRANSFERING_OWNERSHIP());
    } finally {
      this.isTransferring.set(false);
      this.bootstrapService.manageModal('#transferOwnershipConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#transferOwnershipInfoModal', ModalActions.Show);
    }
  }

  public onTransferCancelled(): void {
    this.bootstrapService.manageModal('#transferOwnershipConfirmationModal', ModalActions.Hide);
    this.selectedMemberForTransfer.set(undefined);
  }
  // #endregion Transfer Ownership

  // #region Assign Owner
  public openAssignSelector(): void {
    this.assignMemberList?.loadMembers(false);
    this.bootstrapService.manageModal('#assignOwnershipMemberSelectorModal', ModalActions.Show);
  }

  public onMemberSelectedForAssign(member: MemberModel): void {
    this.selectedMemberForAssign.set(member);
    this.bootstrapService.manageModal('#assignOwnershipMemberSelectorModal', ModalActions.Hide);

    this.assignConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.assignConfirmationModalRef.enableNotes.set(true);

    const confirmationMessageData = { firstName: member.firstName, lastName: member.lastName };
    this.assignConfirmationModalRef.message.set(
      OrganizationsMessagingConstants.ORGANIZATIONS_ASSIGN_OWNER_CONFIRMATION().replaceValuesWithObject(confirmationMessageData)
    );

    this.assignConfirmationModalRef.enableTypeToConfirm.set(false);
    this.assignConfirmationModalRef.confirmButtonText.set('Assign as Owner');
    this.assignConfirmationModalRef.confirmButtonClassName.set('btn-primary');

    this.bootstrapService.manageModal('#assignOwnershipConfirmationModal', ModalActions.Show);
  }

  public async onAssignConfirmed(): Promise<void> {
    const memberToAssign = this.selectedMemberForAssign();
    const ownerRoleId = this.ownerRoleId();
    if (!memberToAssign || !ownerRoleId) return;

    this.isAssigning.set(true);

    try {
      const result = await this.memberService.grantRoleToMember(memberToAssign.memberId, ownerRoleId);

      if (result.success) {
        const activeUser = this.userSessionService.activeUser();

        if (activeUser && activeUser.userId === memberToAssign.userId) {
          await this.authorizationStateService.refreshContext();
        }

        this.currentOwner.set(memberToAssign);

        this.assignInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ASSIGN_OWNER_SUCCESS());
        this.assignInfoModalRef.modalType.set(ModalTypes.Success);
      } else {
        this.assignInfoModalRef.message.set(result.message);
        this.assignInfoModalRef.modalType.set(ModalTypes.Failure);
      }
    } catch (error) {
      console.error(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_ASSIGNING_OWNER(), error);
      this.assignInfoModalRef.modalType.set(ModalTypes.Failure);
      this.assignInfoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_ASSIGNING_OWNER());
    } finally {
      this.isAssigning.set(false);
      this.bootstrapService.manageModal('#assignOwnershipConfirmationModal', ModalActions.Hide);
      this.bootstrapService.manageModal('#assignOwnershipInfoModal', ModalActions.Show);
    }
  }

  public onAssignCancelled(): void {
    this.bootstrapService.manageModal('#assignOwnershipConfirmationModal', ModalActions.Hide);
    this.selectedMemberForAssign.set(undefined);
  }
  // #endregion Assign Owner
}
